import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import { Loader2 } from "lucide-react";
import { Card, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import wbLogoIcon from "@/assets/wb-logo-icon.svg";
import msLogoIcon from "@/assets/ms-logo-icon.svg";

const sessionStorageLoginRedirectKey = "login_redirect";

type LoginSearch = {
  next?: string;
};

export const Route = createFileRoute("/login")({
  component: Login,
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      next: search?.next?.toString(),
    };
  },
});

function Login() {
  const { signinRedirect, isLoading } = useAuth();
  const { next } = Route.useSearch();
  const navigate = useNavigate({ from: "/login" });

  useEffect(() => {
    if (!isLoading) {
      const redirect = sessionStorage.getItem(sessionStorageLoginRedirectKey);
      if (redirect) {
        sessionStorage.removeItem(sessionStorageLoginRedirectKey);
        navigate({ to: redirect });
      }
    }
  }, [isLoading, navigate]);

  function handleLogin() {
    sessionStorage.setItem(sessionStorageLoginRedirectKey, next || "/");
    signinRedirect();
  }

  return (
    <div className="bg-wb-blue-30/5 flex justify-center items-center h-screen p-[4px]">
      <div className="flex flex-col space-y-6">
        <Card className="sm:min-w-md px-6">
          <CardHeader>
            <img className="m-auto w-[2em] h-[2em] mb-4" src={wbLogoIcon} />
            <div className="text-zinc-950 font-bold text-3xl text-center">
              Sign in
            </div>
            <div className="text-zinc-500 text-small text-center">
              Welcome back! Please sign in to continue.
            </div>
          </CardHeader>
          <Button size="lg" onClick={handleLogin} disabled={isLoading}>
            {isLoading ? <Loader2 className="animate-spin mr-[4px]" /> : null}
            <img className="w-[2em] h-[2em]" src={msLogoIcon} />
            Sign in with Microsoft
          </Button>
        </Card>
        <div>
          <div className="text-zinc-500 text-small ">© 2025 Windfall Bio</div>
        </div>
      </div>
    </div>
  );
}
